import React, { useState } from 'react';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item border border-outline-variant/30 rounded-[16px] bg-white overflow-hidden transition-all ${isOpen ? 'active' : ''}`}>
      <button
        className="w-full flex items-center justify-between p-[24px] text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-bold text-on-surface">{question}</span>
        <span className={`material-symbols-outlined faq-icon text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          expand_more
        </span>
      </button>
      <div
        className="faq-answer transition-all duration-300 ease-out"
        style={{ maxHeight: isOpen ? '500px' : '0', overflow: 'hidden' }}
      >
        <div className="p-[24px] pt-[0px] text-[14px] text-on-surface-variant leading-relaxed border-t border-outline-variant/10">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const faqs = [
    {
      question: 'What is the minimum contract period?',
      answer: 'Our minimum contract period for employee transportation services is 3 months. We also offer flexible plans based on your company\'s transportation requirements.'
    },
    {
      question: 'How do you ensure driver verification and safety?',
      answer: '100% of our drivers undergo thorough police verification and background checks. Vehicles are equipped with GPS tracking, SOS buttons, and we provide automated safe-reach calls for all employees.'
    },
    {
      question: 'Can you handle large-scale employee commutes?',
      answer: 'Yes, we manage commutes for companies with 50 to 5,000+ employees using AI-driven route optimization to ensure punctuality and cost-efficiency.'
    },
    {
      question: 'Do you provide executive car rentals for VIP airport transfers?',
      answer: 'Yes, we provide a premium and luxury fleet featuring high-end sedans, luxury SUVs, and executive-class vehicles for VIP airport transfers and corporate travel.'
    },
    {
      question: 'How does the billing and ERP integration work?',
      answer: 'We provide automated, transparent billing based on actual GPS-tracked kilometers. Our system integrates with your ERP for seamless invoicing and clear cost allocation by department.'
    },
    {
      question: 'Do you provide GST-compliant invoices for cab bookings?',
      answer: 'Yes. Every corporate booking is billed with a proper GST invoice showing our GSTIN, the SAC code for passenger transport (9964), the taxable value and the GST charged, so your finance team can record the expense correctly and reconcile it in GSTR-2B.'
    },
    {
      question: 'Can we claim input tax credit (ITC) on corporate cab expenses?',
      answer: 'ITC on hiring motor vehicles for passenger transport is restricted under Section 17(5) of the CGST Act, with specific exceptions (for example where providing the transport is obligatory under a law, or where you supply the same service onward). We issue fully GST-compliant invoices so you can claim credit wherever your business is eligible. Please confirm eligibility with your tax advisor.'
    },
    {
      question: 'How can corporate mobility help reduce operational costs?',
      answer: 'Our route planning and shared mobility solutions help optimize vehicle utilization, reduce transportation inefficiencies, and lower overall commuting expenses.'
    },
    {
      question: 'Can your services support our sustainability and ESG goals?',
      answer: 'Yes, our shared transportation model helps reduce carbon emissions, and we also offer EV-based mobility solutions to support sustainability initiatives.'
    },
    {
      question: 'What happens if a vehicle is delayed or breaks down?',
      answer: 'We maintain a real-time backup fleet and a 24/7 dispatch center. In case of any disruption, our system automatically triggers a replacement vehicle request to ensure minimal impact on employee punctuality.'
    },
    {
      question: 'Do you support multi-city operations for dispersed teams?',
      answer: 'Yes, we provide seamless operational support across multiple cities. You get a unified billing dashboard and a single point of contact, regardless of how many locations your employees are commuting from.'
    },
    {
      question: 'How do you handle shift changes or last-minute requests?',
      answer: 'We offer flexible scheduling and can accommodate shift changes or urgent transportation requests with efficient route adjustments.'
    },
    {
      question: 'What is the process for onboarding a new corporate client?',
      answer: 'We start with a mobility audit of your current travel patterns, followed by a pilot program for a small group. Once validated, we scale to your full workforce with a dedicated onboarding manager to ensure a smooth transition.'
    },
    {
      question: 'How do you manage employee feedback and ride quality?',
      answer: 'Every ride includes a digital feedback mechanism. We monitor these ratings in real-time, and our quality assurance team takes immediate action if service standards drop below our corporate benchmarks.'
    },
    {
      question: 'Do you provide transport for corporate events and off-sites?',
      answer: 'Yes, we offer customized transportation solutions for corporate events, conferences, employee off-sites, and business gatherings.'
    },
    {
      question: 'Can employees track their rides in real time?',
      answer: 'Yes, employees can track their rides in real time, view driver details, and receive trip updates for a seamless commuting experience.'
    },
    {
      question: 'How do you handle peak-hour traffic and ensure punctuality?',
      answer: 'We use predictive traffic analytics to plan routes and buffer times. Our drivers are trained on the most efficient corridors, and our dispatch system monitors traffic patterns in real-time to reroute vehicles as needed.'
    },
    {
      question: 'How do you ensure employee safety during night shifts?',
      answer: 'We provide enhanced safety protocols for night shifts, including mandatory real-time GPS monitoring, secure "safe-reach" confirmations, and 24/7 active monitoring from our command center to ensure every employee arrives home safely.'
    }
  ];

  return (
    <section className="py-[80px] bg-surface" id="faq">
      <div className="max-w-[1152px] mx-auto px-[32px]">
        <div className="text-center mb-[64px]">
          <h2 className="font-headline text-[36px] font-extrabold tracking-tight text-on-surface">Frequently Asked Questions</h2>
          <p className="text-on-surface-variant mt-[16px]">Everything HR and Admin managers need to know about our corporate mobility services.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-x-[48px] gap-y-[16px]">
          <div className="space-y-[16px]">
            {faqs.slice(0, faqs.length / 2).map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
          <div className="space-y-[16px]">
            {faqs.slice(faqs.length / 2).map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>

        <div className="text-center mt-[48px]">
          <a
            href="/gst-invoice-for-cab-bookings/"
            className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
          >
            Read the full GST invoice guide for cab bookings
            <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;


